import { Checkbox, Description, Field, Label } from '@headlessui/react'
import clsx from 'clsx'
import React, { ForwardedRef, forwardRef } from 'react'

import { FormInputProps } from '../fields/types'

export type CheckboxInputProps = FormInputProps & {
  kind?: string
  className?: string
  wrapperClassName?: string
  children?: React.ReactNode
  disabled?: boolean
  onChange?: (checked: boolean, value?: string) => void
  defaultChecked?: boolean
  isPartiallyChecked?: boolean
  checked?: boolean
  value?: string
  descriptionLink?: string | null
  labelExtra?: string | React.ReactNode
}

const CheckboxInput = (
  {
    name,
    label,
    labelExtra,
    description,
    kind,
    className,
    wrapperClassName,
    checked = false,
    isPartiallyChecked = false,
    descriptionLink = null,
    children,
    value,
    hasErrors,
    onChange,
    ...rest
  }: CheckboxInputProps,
  ref: ForwardedRef<HTMLInputElement>,
) => {
  const inputClasses = clsx(
    className,
    'data-[checked]:bg-brand group block size-5 rounded bg-white data-[checked]:ring-[transparent]',
    'shrink-0 cursor-pointer appearance-none p-0 ring ring-1',
    !hasErrors && !rest.disabled && 'ring-slate-400 focus:ring-slate-600',
    isPartiallyChecked && '-checked:absolute -checked:left-[4px] -checked:top-[-1px] -checked:content-[2014]',
    rest.disabled && 'data-[checked]:!bg-brand/40 !cursor-not-allowed !ring-slate-100 !ring-slate-400',
    hasErrors && 'ring-red-500',
  )

  return (
    // Don't use gap utilties here to add space between checkbox and label because user can not click on that gap
    // Use padding utilities on label instead to present a unified clickable checkbox + label area
    <Field className={clsx('flex w-full items-start', wrapperClassName)}>
      <Checkbox
        ref={ref}
        checked={checked || false}
        value={value}
        onChange={(checked: boolean) => {
          onChange?.(checked, value)
        }}
        className={inputClasses}
        {...rest}
      >
        <svg className='stroke-white opacity-0 group-data-[checked]:opacity-100' viewBox='0 0 15 15' fill='none'>
          {isPartiallyChecked ? (
            <rect x='5' y='5' width='5' height='5' fill='white' />
          ) : (
            <path d='M3 8L6 11L11 3.5' strokeWidth={2} strokeLinecap='round' strokeLinejoin='round' />
          )}
        </svg>
      </Checkbox>

      {(label || description) && (
        <div className='w-full pl-3 text-sm'>
          {label && (
            <div className={clsx('font-semibold', rest.disabled && '!cursor-not-allowed', hasErrors && 'text-red')}>
              <Label>{label}</Label>
              <>{labelExtra}</>
            </div>
          )}
          {description && (
            <Description className='text-xs font-normal text-gray-500'>
              {description}
              {descriptionLink && (
                <>
                  {' '}
                  <a
                    href={descriptionLink}
                    target='_blank'
                    rel='noopener noreferrer'
                    className='text-brand hover:underline'
                  >
                    Learn more
                  </a>
                </>
              )}
            </Description>
          )}
        </div>
      )}
    </Field>
  )
}

export default forwardRef<HTMLInputElement, CheckboxInputProps>(CheckboxInput)
